import {
  siteBuilderBlockDelegatesOutlineToColumn,
} from "@webdo/site-templates/builder";

const builderBlockSelector = "[data-builder-block-path]";
const builderColumnSelector = "[data-builder-column-root]";
const builderEditorUiSelector = "[data-builder-editor-ui]";
const builderVisualOutlineSelector = "[data-builder-outline-owner]";
const builderClickableOutlineBlockTypes = new Set([
  "addToCartButton",
  "badge",
  "button",
  "ctaBadge",
  "donationButton",
  "socialLink",
  "supportedPaymentButton",
]);

export type BuilderOutlineOwnership = {
  block: Element;
  context: Element | null;
  own: Element | null;
};

function blockOwnsOutline(block: Element): boolean {
  const type = block.getAttribute("data-builder-block-type");

  return (
    !siteBuilderBlockDelegatesOutlineToColumn(type)
    && block.getAttribute("data-builder-outline-disabled") !== "true"
  );
}

export function builderClickableOutlineOwner(
  block: Element,
  target: Element | null = null,
): Element | null {
  const type = block.getAttribute("data-builder-block-type");
  const targetVisual = target?.closest(builderVisualOutlineSelector) ?? null;

  if (
    targetVisual
    && (
      targetVisual === block
      || targetVisual.closest(builderBlockSelector) === block
    )
  ) {
    return targetVisual;
  }

  if (block.getAttribute("data-builder-outline-owner") === "true") {
    return block;
  }

  const renderedVisual = [
    ...block.querySelectorAll(builderVisualOutlineSelector),
  ].find((candidate) => candidate.closest(builderBlockSelector) === block);

  if (renderedVisual) {
    return renderedVisual;
  }

  if (type && builderClickableOutlineBlockTypes.has(type)) {
    const targetClickable = target?.closest("a, button") ?? null;

    if (
      targetClickable
      && targetClickable.closest(builderBlockSelector) === block
    ) {
      return targetClickable;
    }

    const clickable = [...block.querySelectorAll("a, button")].find(
      (candidate) => candidate.closest(builderBlockSelector) === block,
    );

    if (clickable) {
      return clickable;
    }
  }

  return null;
}

/**
 * Resolves the DOM elements that own a block's visual editor frames.
 *
 * Plain textual children remain the selected model block for inline editing,
 * but their closest visual Column owns the only visible frame. Other blocks
 * retain their own visual frame and use their nearest parent block as context.
 */
export function builderOutlineOwnership(
  target: Element | null,
  preferredOwn: Element | null = null,
): BuilderOutlineOwnership | null {
  if (!target || target.closest(builderEditorUiSelector)) {
    return null;
  }

  const block = target.closest(builderBlockSelector);

  if (!block) {
    return null;
  }

  if (!blockOwnsOutline(block)) {
    return {
      block,
      context: block.closest(builderColumnSelector),
      own: null,
    };
  }

  const visualOwner = preferredOwn
    ?? builderClickableOutlineOwner(block, target)
    ?? block;

  return {
    block,
    context: block.parentElement?.closest(builderBlockSelector) ?? null,
    own: visualOwner,
  };
}

export function builderHoverOutlineOwner(
  target: Element | null,
): Element | null {
  const ownership = builderOutlineOwnership(target);

  return ownership?.own ?? ownership?.context ?? null;
}
